import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, TextField, Grid, FormControl,
    FormLabel, FormHelperText, Select, MenuItem, CircularProgress,
    IconButton, Divider, Chip, Checkbox, FormControlLabel,
    Accordion, AccordionSummary, AccordionDetails,
    ToggleButton, ToggleButtonGroup, InputAdornment,
    Table, TableHead, TableBody, TableRow, TableCell, Pagination,
    Avatar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { quickCreateOrderRequest } from '../../../redux/modules/allStudies/slice';
import { getExamGroupRequest, getExamGroupIdRequest, resetGetExamGroupId } from '../../../redux/modules/exam/slice';
import { getPatientRequest, resetGetPatient } from '../../../redux/modules/radiology/slice';
import { GENDER } from '../../../constants/Constant';
import searchDebounce from '../../../utils/searchDebouncing';

const StudyInfoCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.subtle || theme.palette.grey[50],
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
    fontSize: '0.75rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
}));

const InfoValue = styled(Typography)(({ theme }) => ({
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
}));

const SelectedPatientCard = styled(Box)(({ theme }) => ({
    backgroundColor: 'rgba(105, 203, 249, 0.08)',
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 12,
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
}));

// Ultrasound-specific exam categories
const ULTRASOUND_CATEGORIES = [
    { key: 'OB/GYN Ultrasound', label: 'OB/GYN', color: '#E91E63' },
    { key: 'Abdominal Ultrasound', label: 'Abdominal', color: '#9C27B0' },
    { key: 'Vascular Ultrasound', label: 'Vascular', color: '#2196F3' },
    { key: 'Musculoskeletal Ultrasound', label: 'MSK', color: '#4CAF50' },
    { key: 'Small Parts Ultrasound', label: 'Small Parts', color: '#FF9800' },
    { key: 'Pediatric Ultrasound', label: 'Pediatric', color: '#00BCD4' },
    { key: 'Interventional/Guidance', label: 'Interventional', color: '#795548' },
];

const newPatientSchema = yup.object().shape({
    fName: yup.string().required('First name is required'),
    lName: yup.string().required('Last name is required'),
    dob: yup.string().required('Date of birth is required'),
    gender: yup.string().required('Gender is required'),
    email: yup.string().email('Invalid email').nullable(),
    contactNumber: yup.string().nullable(),
});

const QuickCreateDialog = ({ open, onClose, study }) => {
    const dispatch = useDispatch();
    const { quickCreateLoading } = useSelector(state => state.allStudies);
    const { getGroupRes, getGroupIdRes } = useSelector(state => state.exam);
    const { getPatientRes } = useSelector(state => state.radiology);

    const [patientMode, setPatientMode] = useState('existing'); // 'new' or 'existing'
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [patientSearch, setPatientSearch] = useState('');
    const [patientPage, setPatientPage] = useState(1);
    const patientLimit = 10;
    const initialSearchDone = useRef(false);

    const [expandedCategories, setExpandedCategories] = useState({});
    const [selectedExams, setSelectedExams] = useState([]);
    const [appointmentTime, setAppointmentTime] = useState(null);
    const [referringConsultant, setReferringConsultant] = useState('');

    // Parse patient name from DICOM format (LASTNAME^FIRSTNAME^MIDDLE)
    // Converts ALL CAPS to Title Case
    const toTitleCase = (str) => {
        if (!str) return '';
        return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    };

    const parsePatientName = (dicomName) => {
        if (!dicomName) return { firstName: '', lastName: '' };
        const parts = dicomName.split('^');
        return {
            lastName: toTitleCase(parts[0] || ''),
            firstName: toTitleCase(parts[1] || ''),
        };
    };

    // Parse DOB from DICOM format (YYYYMMDD)
    const parseDicomDate = (dicomDate) => {
        if (!dicomDate || dicomDate.length !== 8) return null;
        return `${dicomDate.substring(0, 4)}-${dicomDate.substring(4, 6)}-${dicomDate.substring(6, 8)}`;
    };

    // Parse DICOM StudyTime (HHMMSS or HHMMSS.ffffff) to moment
    const parseDicomTime = (dicomTime) => {
        if (!dicomTime) return null;
        const clean = dicomTime.split('.')[0]; // remove fractional seconds
        if (clean.length < 4) return null;
        const hh = clean.substring(0, 2);
        const mm = clean.substring(2, 4);
        const ss = clean.length >= 6 ? clean.substring(4, 6) : '00';
        return moment(`${hh}:${mm}:${ss}`, 'HH:mm:ss');
    };

    const { control, handleSubmit, formState: { errors }, setValue, reset } = useForm({
        resolver: patientMode === 'new' ? yupResolver(newPatientSchema) : undefined,
        defaultValues: {
            fName: '',
            lName: '',
            dob: moment().format('YYYY-MM-DD'),
            gender: '',
            email: '',
            contactNumber: '',
        },
    });

    const performPatientSearch = useCallback((query) => {
        dispatch(getPatientRequest(query));
    }, [dispatch]);

    const debouncedSearch = useCallback(searchDebounce(performPatientSearch, 300), [performPatientSearch]);

    // Load exam groups on mount
    useEffect(() => {
        if (open) {
            dispatch(getExamGroupRequest());
        }
    }, [open, dispatch]);

    // Pre-fill form when study changes
    useEffect(() => {
        if (open && study) {
            const parsed = parsePatientName(study.patientName);
            setValue('fName', parsed.firstName);
            setValue('lName', parsed.lastName);

            if (study.patientBirthDate) {
                const dob = parseDicomDate(study.patientBirthDate);
                if (dob) setValue('dob', dob);
            }

            // Try to determine gender from DICOM
            if (study.patientSex) {
                const sex = study.patientSex.toUpperCase();
                if (sex === 'M') setValue('gender', 'male');
                else if (sex === 'F') setValue('gender', 'female');
            }

            // Pre-fill appointment time from DICOM StudyTime
            if (study.studyTime) {
                const time = parseDicomTime(study.studyTime);
                if (time && time.isValid()) setAppointmentTime(time);
            }

            // Pre-fill referring consultant from DICOM ReferringPhysicianName
            if (study.referringPhysicianName) {
                setReferringConsultant(toTitleCase(study.referringPhysicianName.replace(/\^/g, ' ').trim()));
            }

            // Auto-search for existing patients using DICOM first name
            if (!initialSearchDone.current && parsed.firstName) {
                setPatientSearch(parsed.firstName);
                dispatch(getPatientRequest({ name: parsed.firstName, page: 1, limit: patientLimit }));
                initialSearchDone.current = true;
            }
        }
    }, [open, study, setValue, dispatch]);

    // Cleanup on close
    useEffect(() => {
        if (!open) {
            reset();
            setExpandedCategories({});
            setSelectedExams([]);
            setSelectedPatient(null);
            setPatientMode('existing');
            setPatientSearch('');
            setPatientPage(1);
            setAppointmentTime(null);
            setReferringConsultant('');
            initialSearchDone.current = false;
            dispatch(resetGetExamGroupId());
            dispatch(resetGetPatient());
        }
    }, [open, reset, dispatch]);

    // Fetch exams for expanded categories
    useEffect(() => {
        const expandedKeys = Object.keys(expandedCategories).filter(k => expandedCategories[k]);
        if (expandedKeys.length > 0) {
            dispatch(getExamGroupIdRequest(expandedKeys));
        }
    }, [expandedCategories, dispatch]);

    const handlePatientModeChange = (event, newMode) => {
        if (newMode !== null) {
            setPatientMode(newMode);
            setSelectedPatient(null);
        }
    };

    const handlePatientSearchChange = (event) => {
        const value = event.target.value;
        setPatientSearch(value);
        setPatientPage(1);
        debouncedSearch({ name: value, page: 1, limit: patientLimit });
    };

    const handlePatientPageChange = (event, value) => {
        setPatientPage(value);
        dispatch(getPatientRequest({ name: patientSearch, page: value, limit: patientLimit }));
    };

    const handleSelectPatient = (patient) => {
        setSelectedPatient(patient);
    };

    const handleCategoryToggle = (key) => {
        setExpandedCategories(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleExamToggle = (exam, group) => {
        setSelectedExams(prev => {
            const exists = prev.find(e => e.exam._id === exam._id);
            if (exists) {
                return prev.filter(e => e.exam._id !== exam._id);
            } else {
                return [...prev, { exam, group }];
            }
        });
    };

    const isExamSelected = (examId) => {
        return selectedExams.some(e => e.exam._id === examId);
    };

    const onSubmit = (data) => {
        if (!study?.orthancStudyId) return;

        // For existing patient mode, validate selection
        if (patientMode === 'existing' && !selectedPatient?._id) return;

        // Group exams by their group (if any selected)
        let examList = [];
        if (selectedExams.length > 0) {
            const examListByGroup = {};
            selectedExams.forEach(({ exam, group }) => {
                if (!examListByGroup[group._id]) {
                    examListByGroup[group._id] = {
                        group: { _id: group._id, name: group.name },
                        list: [],
                        examListModality: [],
                    };
                }
                examListByGroup[group._id].list.push({ _id: exam._id, name: exam.name });
            });
            examList = Object.values(examListByGroup);
        }

        // Use the time picker value if set, otherwise fall back to raw DICOM StudyTime
        let studyTimeValue = study.studyTime || '';
        if (appointmentTime && moment.isMoment(appointmentTime) && appointmentTime.isValid()) {
            studyTimeValue = appointmentTime.format('HHmmss');
        }

        const payload = {
            orthancStudyId: study.orthancStudyId,
            examList,
            studyDateTime: study.studyDate,
            studyTime: studyTimeValue,
        };

        // Include referring consultant if provided
        if (referringConsultant && referringConsultant.trim()) {
            payload.referringConsultant = referringConsultant.trim();
        }

        if (patientMode === 'existing') {
            payload.patientId = selectedPatient._id;
        } else {
            payload.patient = {
                fName: data.fName,
                lName: data.lName,
                dob: data.dob,
                gender: data.gender,
                email: data.email || null,
                contactNumber: data.contactNumber || null,
            };
        }

        dispatch(quickCreateOrderRequest(payload));
    };

    const handleFormSubmit = (e) => {
        if (patientMode === 'existing') {
            e.preventDefault();
            onSubmit({});
        } else {
            handleSubmit(onSubmit)(e);
        }
    };

    if (!study) return null;

    // Get exam groups from response
    const examGroups = getGroupIdRes?.data || [];

    const patientCount = getPatientRes?.dataCount || 0;
    const patientPageCount = Math.ceil(patientCount / patientLimit) || 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={600}>
                    Quick Create Order
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <form onSubmit={handleFormSubmit}>
                <DialogContent>
                    {/* Study Information */}
                    <StudyInfoCard>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                            Study Information
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2 }}>
                            <Box>
                                <InfoLabel>Study Date & Time</InfoLabel>
                                <InfoValue>
                                    {study.studyDate ? moment(study.studyDate).format('MMM DD, YYYY') : '—'}
                                    {study.studyTime ? ` at ${study.studyTime.substring(0, 2)}:${study.studyTime.substring(2, 4)}` : ''}
                                </InfoValue>
                            </Box>
                            <Box>
                                <InfoLabel>Modality</InfoLabel>
                                <InfoValue>{study.modality || 'US'}</InfoValue>
                            </Box>
                            <Box>
                                <InfoLabel>Images</InfoLabel>
                                <InfoValue>{study.instanceCount || 0}</InfoValue>
                            </Box>
                            <Box>
                                <InfoLabel>Description</InfoLabel>
                                <InfoValue>{study.studyDescription || '—'}</InfoValue>
                            </Box>
                        </Box>
                    </StudyInfoCard>

                    {/* Appointment Time & Referring Consultant */}
                    <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <TimePicker
                                    label="Appointment Time (from study)"
                                    value={appointmentTime}
                                    onChange={(val) => setAppointmentTime(val)}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: 'small',
                                            helperText: appointmentTime ? '' : 'No time detected from study',
                                            InputProps: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <AccessTimeIcon fontSize="small" color="action" />
                                                    </InputAdornment>
                                                ),
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Referring Consultant / GP (Optional)"
                                value={referringConsultant}
                                onChange={(e) => setReferringConsultant(e.target.value)}
                                placeholder="e.g. Dr. Smith"
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* Patient Mode Toggle */}
                    <SectionTitle>Patient Information</SectionTitle>
                    <ToggleButtonGroup
                        value={patientMode}
                        exclusive
                        onChange={handlePatientModeChange}
                        size="small"
                        sx={{ mb: 2 }}
                    >
                        <ToggleButton value="new">
                            <PersonAddIcon sx={{ mr: 1 }} fontSize="small" />
                            New Patient
                        </ToggleButton>
                        <ToggleButton value="existing">
                            <PersonIcon sx={{ mr: 1 }} fontSize="small" />
                            Select Existing
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {patientMode === 'new' ? (
                        /* New Patient Form */
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="fName"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            label="First Name *"
                                            error={Boolean(errors.fName)}
                                            helperText={errors.fName?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="lName"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            label="Last Name *"
                                            error={Boolean(errors.lName)}
                                            helperText={errors.lName?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="dob"
                                    control={control}
                                    render={({ field }) => (
                                        <LocalizationProvider dateAdapter={AdapterMoment}>
                                            <DatePicker
                                                label="Date of Birth *"
                                                value={field.value ? moment(field.value) : null}
                                                onChange={(date) => field.onChange(date ? moment(date).format('YYYY-MM-DD') : '')}
                                                disableFuture
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        size: 'small',
                                                        error: Boolean(errors.dob),
                                                        helperText: errors.dob?.message,
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="gender"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControl fullWidth size="small" error={Boolean(errors.gender)}>
                                            <FormLabel sx={{ fontSize: '0.75rem', mb: 0.5 }}>Gender *</FormLabel>
                                            <Select {...field} displayEmpty>
                                                <MenuItem value="" disabled>Select Gender</MenuItem>
                                                <MenuItem value={GENDER.MALE}>Male</MenuItem>
                                                <MenuItem value={GENDER.FEMALE}>Female</MenuItem>
                                                <MenuItem value={GENDER.OTHER}>Other</MenuItem>
                                            </Select>
                                            {errors.gender && <FormHelperText>{errors.gender.message}</FormHelperText>}
                                        </FormControl>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="contactNumber"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            label="Phone (Optional)"
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="email"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            size="small"
                                            label="Email (Optional)"
                                            type="email"
                                            error={Boolean(errors.email)}
                                            helperText={errors.email?.message}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    ) : (
                        /* Select Existing Patient */
                        <Box>
                            {selectedPatient ? (
                                <SelectedPatientCard>
                                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                                        <PersonIcon />
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography fontWeight={600}>
                                            {selectedPatient.fName} {selectedPatient.lName}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {selectedPatient.gender && `${selectedPatient.gender} | `}
                                            {selectedPatient.dob ? `DOB: ${moment.utc(selectedPatient.dob).format('DD-MM-YYYY')}` : ''}
                                            {selectedPatient.mrn && ` | MRN: ${selectedPatient.mrn}`}
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => {
                                            setSelectedPatient(null);
                                            dispatch(getPatientRequest({ name: '', page: 1, limit: patientLimit }));
                                        }}
                                    >
                                        Change
                                    </Button>
                                </SelectedPatientCard>
                            ) : (
                                <>
                                    <TextField
                                        fullWidth
                                        placeholder="Search patient by name..."
                                        value={patientSearch}
                                        onChange={handlePatientSearchChange}
                                        size="small"
                                        sx={{ mb: 2 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>First Name</TableCell>
                                                    <TableCell>Last Name</TableCell>
                                                    <TableCell>DOB</TableCell>
                                                    <TableCell>Gender</TableCell>
                                                    <TableCell>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {getPatientRes?.data?.length > 0 ? (
                                                    getPatientRes.data.map(patient => (
                                                        <TableRow key={patient._id} hover>
                                                            <TableCell>{patient.fName}</TableCell>
                                                            <TableCell>{patient.lName || '—'}</TableCell>
                                                            <TableCell>{patient.dob ? moment.utc(patient.dob).format('DD-MM-YYYY') : '—'}</TableCell>
                                                            <TableCell sx={{ textTransform: 'capitalize' }}>{patient.gender || '—'}</TableCell>
                                                            <TableCell>
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    onClick={() => handleSelectPatient(patient)}
                                                                >
                                                                    Select
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={5} align="center">
                                                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                                                {patientSearch ? 'No patients found' : 'Search for a patient by name'}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                        {patientCount > patientLimit && (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                                <Pagination
                                                    count={patientPageCount}
                                                    page={patientPage}
                                                    onChange={handlePatientPageChange}
                                                    size="small"
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* Exam Selection */}
                    <SectionTitle>Select Exams (Optional)</SectionTitle>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Click a category to expand and select specific exams. You can add exams later when editing the report.
                    </Typography>

                    {/* Selected Exams Display */}
                    {selectedExams.length > 0 && (
                        <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                            <Typography variant="subtitle2" fontWeight={600} color="primary.dark" sx={{ mb: 1 }}>
                                Selected Exams ({selectedExams.length})
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {selectedExams.map(({ exam, group }) => (
                                    <Chip
                                        key={exam._id}
                                        label={exam.name}
                                        size="small"
                                        onDelete={() => handleExamToggle(exam, group)}
                                        sx={{ bgcolor: 'background.paper' }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Category Accordions */}
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {ULTRASOUND_CATEGORIES.map(category => (
                            <Accordion
                                key={category.key}
                                expanded={expandedCategories[category.key] || false}
                                onChange={() => handleCategoryToggle(category.key)}
                                sx={{ mb: 1, borderRadius: 2, '&:before': { display: 'none' } }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        borderRadius: 2,
                                        '& .MuiAccordionSummary-content': { my: 1 }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                bgcolor: category.color,
                                            }}
                                        />
                                        <Typography fontWeight={500}>{category.label}</Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {examGroups
                                            .filter(g => g.name === category.key)
                                            .flatMap(g => g.list?.map(exam => ({
                                                exam,
                                                group: { _id: g._id, name: g.name }
                                            })) || [])
                                            .map(({ exam, group }) => (
                                                <FormControlLabel
                                                    key={exam._id}
                                                    control={
                                                        <Checkbox
                                                            checked={isExamSelected(exam._id)}
                                                            onChange={() => handleExamToggle(exam, group)}
                                                            size="small"
                                                        />
                                                    }
                                                    label={<Typography variant="body2">{exam.name}</Typography>}
                                                    sx={{
                                                        m: 0,
                                                        p: 0.5,
                                                        borderRadius: 1,
                                                        bgcolor: isExamSelected(exam._id) ? 'primary.light' : 'transparent',
                                                    }}
                                                />
                                            ))}
                                        {examGroups.filter(g => g.name === category.key).length === 0 && (
                                            <Typography variant="body2" color="text.secondary">
                                                Loading exams...
                                            </Typography>
                                        )}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>

                </DialogContent>

                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button onClick={onClose} disabled={quickCreateLoading}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={quickCreateLoading || (patientMode === 'existing' && !selectedPatient)}
                        startIcon={quickCreateLoading ? <CircularProgress size={16} /> : null}
                    >
                        {quickCreateLoading ? 'Creating...' : 'Create Order & Link Study'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default QuickCreateDialog;
